import type { Course, Unit } from '@yuan-math/curriculum';

/**
 * Courses are the *packaging* of the curriculum: a familiar, grade-shaped route
 * through concepts that are themselves grade-independent. A student can follow
 * a course, or ignore courses entirely and walk the Math Map. Both reach the
 * same lessons.
 */
export const COURSES: Course[] = [
  {
    id: 'course-grade-3',
    title: 'Grade 3 Mathematics',
    subtitle: 'Multiplication, the first fractions, and the measured world',
    description:
      'Place value, the four operations and the strategies behind them, fractions as numbers, time, mass, shape, area, and the first graphs.',
    level: 'grade-3',
    strand: 'arithmetic',
    status: 'available',
    unitIds: [
      'unit-number-sense',
      'unit-whole-number-operations',
      'unit-multiplication-strategies',
      'unit-problem-solving',
      'unit-fractions-meaning',
      'unit-measurement',
      'unit-shapes',
      'unit-time-measurement',
      'unit-data-grade-3',
    ],
  },
  {
    id: 'course-grade-4',
    title: 'Grade 4 Mathematics',
    subtitle: 'Methods, not just facts',
    description:
      'Multi-digit multiplication and long division, primes and factors, equivalent fractions and the first fraction arithmetic, decimals as fractions, measurement conversion, angles and symmetry.',
    level: 'grade-4',
    strand: 'arithmetic',
    status: 'available',
    unitIds: [
      'unit-multi-digit-arithmetic',
      'unit-factors',
      'unit-fractions-equivalence',
      'unit-fractions-compare',
      'unit-fraction-operations',
      'unit-decimals',
      'unit-measure-geometry-4',
    ],
  },
  {
    id: 'course-grade-5',
    title: 'Grade 5 Mathematics',
    subtitle: 'The year the whole number stops being the unit',
    description:
      'Powers of ten and decimal arithmetic, fractions as divisions, mixed numbers, multiplication that shrinks and division that grows, expressions with brackets, volume, the coordinate plane, and the shape hierarchy.',
    level: 'grade-5',
    strand: 'numbers',
    status: 'available',
    unitIds: [
      'unit-powers-of-ten-5',
      'unit-decimals',
      'unit-decimal-operations-5',
      'unit-fractions-arithmetic',
      'unit-fraction-scaling-5',
      'unit-intro-algebra',
      'unit-volume-5',
      'unit-coordinate-plane-5',
      'unit-classify-shapes-5',
    ],
  },
  {
    id: 'course-fractions-mastery',
    title: 'Fractions, End to End',
    subtitle: 'The whole strand in one place',
    description:
      'Every fraction concept from "what a fraction is" through to multiplying them — for a student who wants to close this gap once and for all.',
    level: 'grade-4',
    strand: 'fractions',
    status: 'available',
    unitIds: [
      'unit-fractions-meaning',
      'unit-fractions-equivalence',
      'unit-fractions-compare',
      'unit-fraction-operations',
      'unit-fractions-arithmetic',
    ],
  },

  /* Announced, not yet built. Present so the road ahead is visible. */
  {
    id: 'course-grade-6',
    title: 'Grade 6 Mathematics',
    subtitle: 'Ratios, negative numbers and the start of real algebra',
    description:
      'Dividing fractions, decimal arithmetic, negative numbers, exponents and common factors, ratio and percent, expressions, inequalities and relationships between two quantities, area and volume, and the first statistics.',
    level: 'grade-6',
    strand: 'numbers',
    status: 'available',
    unitIds: [
      'unit-fraction-division',
      'unit-decimal-operations',
      'unit-integers',
      'unit-exponents',
      'unit-ratios',
      'unit-percent',
      'unit-expressions',
      'unit-inequalities-relationships',
      'unit-coordinate-geometry',
      'unit-area-volume',
      'unit-statistics',
    ],
  },
  {
    id: 'course-ratio-percent',
    title: 'Ratio, Rate and Percent',
    subtitle: 'The whole of proportional reasoning',
    description:
      'The Grade 6 idea that comparison can be multiplicative rather than additive — for a student who needs it to finally click.',
    level: 'grade-6',
    strand: 'numbers',
    status: 'available',
    unitIds: ['unit-ratios', 'unit-percent'],
  },
  {
    id: 'course-grade-7',
    title: 'Grade 7 Mathematics',
    subtitle: 'Signed arithmetic, proportion and the first real algebra',
    description:
      'Operations with rational numbers, proportional relationships and percent change, linear expressions, multi-step equations and inequalities, angles, circles and scale drawings, surface area, probability of compound events, and the first work with samples.',
    level: 'grade-7',
    strand: 'algebra',
    status: 'available',
    unitIds: [
      'unit-rational-numbers',
      'unit-proportional-reasoning',
      'unit-linear-expressions',
      'unit-equations',
      'unit-angles-circles',
      'unit-scale-drawings',
      'unit-solids',
      'unit-probability',
      'unit-samples',
    ],
  },
  {
    id: 'course-prealgebra',
    title: 'Pre-Algebra',
    subtitle: 'The bridge to formal algebra',
    description:
      'Everything between arithmetic and Algebra I: signed numbers, exponents, expressions, multi-step equations, inequalities and the coordinate plane.',
    level: 'grade-7',
    strand: 'algebra',
    status: 'available',
    unitIds: [
      'unit-integers',
      'unit-exponents',
      'unit-expressions',
      'unit-rational-numbers',
      'unit-linear-expressions',
      'unit-equations',
    ],
  },
  {
    id: 'course-proportional-reasoning',
    title: 'Proportional Reasoning, End to End',
    subtitle: 'Ratio to percent change, in one run',
    description:
      'The thread that runs from "3 to 2" all the way to compound discounts and interest — for a student who needs proportion to finally become automatic.',
    level: 'grade-7',
    strand: 'numbers',
    status: 'available',
    unitIds: ['unit-ratios', 'unit-percent', 'unit-proportional-reasoning'],
  },
  {
    id: 'course-grade-8',
    title: 'Grade 8 Mathematics',
    subtitle: 'Powers, functions and the first proofs',
    description:
      'The laws of exponents and scientific notation, square and cube roots, linear equations with one solution or none at all, functions linear and nonlinear, slope, systems of equations, the Pythagorean theorem and the distance it measures, transformations, parallel lines and similar triangles, the round solids, scatter plots, two-way tables and the probability a long run of trials actually produces.',
    level: 'grade-8',
    strand: 'functions',
    status: 'available',
    unitIds: [
      'unit-exponent-laws',
      'unit-roots',
      'unit-linear-equations',
      'unit-functions',
      'unit-transformations',
      'unit-angle-geometry',
      'unit-bivariate-data',
      'unit-chance',
    ],
  },
  {
    id: 'course-algebra-readiness',
    title: 'Ready for Algebra',
    subtitle: 'The whole road from arithmetic to Algebra I',
    description:
      'Everything a student needs before formal algebra, in order: signed numbers, exponents and roots, expressions, equations and inequalities, functions and slope, and systems.',
    level: 'grade-8',
    strand: 'algebra',
    status: 'available',
    unitIds: [
      'unit-integers',
      'unit-expressions',
      'unit-rational-numbers',
      'unit-linear-expressions',
      'unit-equations',
      'unit-linear-equations',
      'unit-exponent-laws',
      'unit-roots',
      'unit-functions',
    ],
  },
  {
    id: 'course-algebra-1',
    title: 'Algebra I',
    subtitle: 'Where an expression becomes something you can reshape',
    description:
      'The real numbers and the properties that licence every rearrangement; equations with fractions, compound inequalities and the three forms of a line; rearranging formulas, domain and range, and inequalities that shade a region; polynomials, factoring and radicals; quadratic equations, parabolas, completing the square and the quadratic formula; exponential growth, sequences, rational expressions, absolute value and standard deviation.',
    level: 'grade-9',
    strand: 'algebra',
    status: 'available',
    unitIds: [
      'unit-real-numbers',
      'unit-linear-forms',
      'unit-algebra-foundations',
      'unit-polynomials',
      'unit-quadratics',
      'unit-growth',
      'unit-rational-algebra',
      'unit-data-spread',
    ],
  },
  {
    id: 'course-quadratics',
    title: 'Quadratics, End to End',
    subtitle: 'From factoring to the formula',
    description:
      'The whole quadratic story in one run — factorising, the zero-product rule, the parabola behind it, completing the square, and the formula that finishes the job.',
    level: 'grade-9',
    strand: 'algebra',
    status: 'available',
    unitIds: ['unit-polynomials', 'unit-quadratics'],
  },
  {
    id: 'course-geometry',
    title: 'Geometry',
    subtitle: 'The course where answers start needing reasons',
    description:
      'Conditional statements and proof, parallel lines, triangle congruence and proportionality, compass-and-straightedge construction, similarity, quadrilaterals, circle theorems, chords and secants, arcs and sectors, coordinate geometry, right-triangle trigonometry in both directions, transformations and the measurement of solids.',
    level: 'grade-10',
    strand: 'geometry',
    status: 'available',
    unitIds: [
      'unit-proof',
      'unit-triangles',
      'unit-polygons-circles',
      'unit-coordinate-trig',
      'unit-transformations',
      'unit-solids',
    ],
  },
  {
    id: 'course-proof',
    title: 'Learning to Prove',
    subtitle: 'Why measuring is not knowing',
    description:
      'The reasoning half of geometry on its own — what a proof is, how a statement differs from its converse, inverse and contrapositive, the congruence arguments that everything else is built from, and the constructions those arguments justify.',
    level: 'grade-10',
    strand: 'geometry',
    status: 'available',
    unitIds: ['unit-proof', 'unit-triangles'],
  },
  {
    id: 'course-algebra-2',
    title: 'Algebra II',
    subtitle: 'Where the separate topics turn out to be one topic',
    description:
      'Rational exponents and complex numbers; polynomial, rational, radical and piecewise functions; transformations, compositions, inverses, logarithms and the models they solve; inequalities and systems with a curve; the unit circle, the graphs of sine, cosine and tangent; sequences defined by recursion; and the two statistics that a standard deviation makes possible.',
    level: 'grade-11',
    strand: 'functions',
    status: 'available',
    unitIds: [
      'unit-complex-polynomials',
      'unit-rational-radical',
      'unit-function-shapes',
      'unit-logarithms',
      'unit-systems-inequalities',
      'unit-sequences-11',
      'unit-trig-functions',
      'unit-data-eleven',
    ],
  },
  {
    id: 'course-functions',
    title: 'Functions, End to End',
    subtitle: 'One curve, moved',
    description:
      'The thread that runs from "a function is a machine" to combining, shifting, stretching, inverting and unrolling one — for a student who wants the whole idea rather than a topic at a time.',
    level: 'grade-11',
    strand: 'functions',
    status: 'available',
    unitIds: ['unit-functions', 'unit-function-shapes', 'unit-logarithms'],
  },
  {
    id: 'course-precalculus',
    title: 'Precalculus',
    subtitle: 'The year the answer stops being a number',
    description:
      'Trigonometric identities, the addition, double- and half-angle formulas, trigonometric equations and the sine and cosine rules; series, the sums that never finish and the induction that proves them; counting, the binomial theorem and probability distributions; the conic sections, vectors and the dot product, polar coordinates and the complex plane; and finally limits, continuity and the rate of change at an instant.',
    level: 'grade-12',
    strand: 'functions',
    status: 'available',
    unitIds: ['unit-trig-complete', 'unit-series', 'unit-counting', 'unit-vectors-conics', 'unit-limits'],
  },
  {
    id: 'course-counting-chance',
    title: 'Counting and Chance',
    subtitle: 'How many ways, and how likely',
    description:
      'Probability from its first definition through to distributions — for a student who wants the counting arguments underneath it rather than a rule for each kind of question.',
    level: 'grade-12',
    strand: 'probability',
    status: 'available',
    unitIds: ['unit-probability', 'unit-counting'],
  },
  {
    id: 'course-calculus',
    title: 'Calculus',
    subtitle: 'The other Grade 12',
    description:
      'The pathway that starts where precalculus stops: continuity and the three ways a limit can fail, the derivative and the rules for differentiating anything built out of anything else, what a derivative says about the shape of a curve and about a moving body, and then the reverse — antiderivatives, the area under a curve, and the theorem that says those two are the same question.',
    level: 'grade-12',
    strand: 'calculus',
    status: 'available',
    unitIds: ['unit-limits', 'unit-derivatives', 'unit-derivative-applications', 'unit-integrals'],
  },
  {
    id: 'course-statistics',
    title: 'Probability and Statistics',
    subtitle: 'The mathematics of what you did not measure',
    description:
      'Sample spaces and the rules that govern events, conditional probability and the independence that is a special case of it, Bayes\u2019 theorem and the counting arguments underneath a probability too large to list; random variables discrete and continuous, their expected value, variance and standard deviation; the binomial, geometric, Poisson and normal distributions and what each one is for; and then inference \u2014 sampling distributions, confidence intervals, hypothesis tests, correlation and the least-squares line.',
    level: 'university',
    strand: 'statistics',
    status: 'available',
    unitIds: [
      'unit-probability-rules',
      'unit-conditional-probability',
      'unit-random-variables',
      'unit-distributions',
      'unit-inference',
      'unit-two-variables',
    ],
  },
  {
    id: 'course-calculus-1',
    title: 'Calculus II',
    subtitle: 'The first year of university',
    description:
      'Where single-variable calculus goes after the fundamental theorem: substitution, parts, trigonometric methods and partial fractions; integrals that run to infinity; arc length, surface area, work and centre of mass; sequences, series and the tests that decide them; power series and Taylor expansions; and curves described by a parameter or by an angle.',
    level: 'university',
    strand: 'calculus',
    status: 'available',
    unitIds: [
      'unit-integration-techniques',
      'unit-integral-applications',
      'unit-infinite-series',
      'unit-power-series',
      'unit-parametric-polar',
    ],
  },
  {
    id: 'course-linear-algebra',
    title: 'Linear Algebra',
    subtitle: 'The other thing school was a run-up to',
    description:
      'The second university branch out of Grade 12, and the one that has nothing to do with calculus: vectors in as many dimensions as you like, linear combinations and the span they reach, independence, matrices and the row reduction that solves any system at all, inverses and determinants, rank and the shape a solution set takes, vector spaces and their bases, coordinates in a basis of your choosing, linear transformations with their kernels and ranges, and finally eigenvalues — the directions a transformation leaves alone, and what they say about the long run.',
    level: 'university',
    strand: 'linear-algebra',
    status: 'available',
    unitIds: [
      'unit-la-vectors',
      'unit-la-matrices',
      'unit-la-systems',
      'unit-la-vector-spaces',
      'unit-la-transformations',
      'unit-la-eigen',
    ],
  },
  {
    id: 'course-competition',
    title: 'Competition Mathematics',
    subtitle: 'AMC, AIME and olympiad problem solving',
    description: 'Problem-solving technique in algebra, number theory, combinatorics and geometry.',
    level: 'grade-10',
    strand: 'competition',
    status: 'planned',
    unitIds: [],
  },
];

export const UNITS: Unit[] = [
  /* --------------------------------------------------------- precalculus */
  {
    id: 'unit-trig-complete',
    courseId: 'course-precalculus',
    title: 'Trigonometry, Finished',
    description:
      'Statements true for every angle, the expansions of sin(A + B) and cos(A + B), equations with infinitely many answers, and the two rules that free trigonometry from the right triangle.',
    lessonIds: [
      'trig-identities-01',
      'trig-addition-formulas-01',
      'half-angle-formulas-01',
      'trig-equations-01',
      'law-of-sines-cosines-01',
    ],
  },
  {
    id: 'unit-series',
    courseId: 'course-precalculus',
    title: 'Sums and Proofs Without End',
    description:
      'Adding a sequence up — including one with no last term, which nevertheless has an exact total — and the argument that settles a claim about every whole number in a page.',
    lessonIds: ['series-01', 'mathematical-induction-01'],
  },
  {
    id: 'unit-counting',
    courseId: 'course-precalculus',
    title: 'Counting Without Listing',
    description:
      'How many ways, how a bracket to the twentieth power is a counting problem, and how a random quantity is described completely.',
    lessonIds: ['combinatorics-intro-01', 'binomial-theorem-01', 'probability-distributions-01'],
  },
  {
    id: 'unit-vectors-conics',
    courseId: 'course-precalculus',
    title: 'Conics, Vectors and Direction',
    description:
      'Four curves cut from one cone, quantities that carry a direction as well as a size, and the coordinate system built from that pair.',
    lessonIds: ['conic-sections-01', 'vectors-01', 'dot-product-01', 'polar-coordinates-01', 'complex-polar-01'],
  },
  {
    id: 'unit-limits',
    courseId: 'course-precalculus',
    title: 'The Edge of Calculus',
    description:
      'Naming the idea the rest of the course has been using — a value approached rather than reached — the three ways it can fail, and the first question that cannot be answered without it.',
    lessonIds: ['limits-01', 'continuity-01', 'rates-of-change-01'],
  },

  /* ------------------------------------------------------------- calculus */
  /*
   * `unit-limits` above is shared: it is the last unit of precalculus and the
   * first of calculus, because the two courses are pathways out of the same
   * point rather than a sequence. The three below belong to calculus alone.
   */
  {
    id: 'unit-derivatives',
    courseId: 'course-calculus',
    title: 'The Derivative',
    description:
      'Turning the slope at one point into a function giving the slope at every point, and then the three rules that differentiate anything assembled out of simpler pieces.',
    lessonIds: ['derivatives-01', 'derivative-rules-01', 'implicit-differentiation-01'],
  },
  {
    id: 'unit-derivative-applications',
    courseId: 'course-calculus',
    title: 'What the Derivative Tells You',
    description:
      'Reading the shape of a curve off its slope function, finding the best answer rather than a good one, and following a moving body through position, velocity and acceleration.',
    lessonIds: ['derivative-applications-01', 'optimization-01', 'motion-derivatives-01'],
  },
  {
    id: 'unit-integrals',
    courseId: 'course-calculus',
    title: 'Adding Up the Infinitely Small',
    description:
      'Differentiation run backwards, the area under a curve as a limit of rectangles, and the theorem that says — surprisingly — that those are the same calculation.',
    lessonIds: ['antiderivatives-01', 'integrals-01', 'fundamental-theorem-01'],
  },

  /* --------------------------------------------- probability and statistics */
  /*
   * Six units, and the order is the argument of the course. The first two build
   * probability into something that can carry a random quantity; the third
   * builds the random quantity; the fourth names the four distributions worth
   * recognising on sight; and only then does the fifth turn the machinery on
   * the question statistics was invented for \u2014 what a sample says about a
   * population it was drawn from. The sixth is the same inference asked about
   * two measurements at once rather than one.
   */
  {
    id: 'unit-probability-rules',
    courseId: 'course-statistics',
    title: 'The Rules of Chance',
    description:
      'Everything that could happen, written down; the events built out of it with "and", "or" and "not"; and the probabilities that are ratios of counts too large to list.',
    lessonIds: ['sample-spaces-01', 'probability-events-01', 'counting-probability-01'],
  },
  {
    id: 'unit-conditional-probability',
    courseId: 'course-statistics',
    title: 'What Knowing Changes',
    description:
      'Probability after information arrives, the case where information changes nothing, and the theorem that runs a conditional probability backwards.',
    lessonIds: ['conditional-probability-01', 'independence-01', 'bayes-theorem-01'],
  },
  {
    id: 'unit-random-variables',
    courseId: 'course-statistics',
    title: 'Random Variables',
    description:
      'A number decided by chance, and the three summaries of one: where it lands on average, how far from that it typically strays, and what happens when two of them are added.',
    lessonIds: [
      'discrete-random-variables-01',
      'expected-value-01',
      'random-variable-variance-01',
      'random-variable-sd-01',
      'continuous-random-variables-01',
    ],
  },
  {
    id: 'unit-distributions',
    courseId: 'course-statistics',
    title: 'Four Distributions Worth Knowing',
    description:
      'Successes in a fixed number of tries, the wait for the first one, counts with no fixed number of tries at all, and the bell curve that turns up whenever many small effects add.',
    lessonIds: [
      'binomial-distribution-01',
      'geometric-distribution-01',
      'poisson-distribution-01',
      'normal-random-variable-01',
    ],
  },
  {
    id: 'unit-inference',
    courseId: 'course-statistics',
    title: 'From a Sample to a Population',
    description:
      'The one idea the rest of statistics is arithmetic on: a sample mean varies, its variation is predictable, and both an interval estimate and a test of a claim are readings of that variation.',
    lessonIds: ['sampling-distributions-01', 'confidence-intervals-01', 'hypothesis-testing-01'],
  },
  {
    id: 'unit-two-variables',
    courseId: 'course-statistics',
    title: 'Two Measurements at Once',
    description:
      'Association measured in units-free terms, and the line that is that measurement put back into the units of the data.',
    lessonIds: ['correlation-01', 'linear-regression-01'],
  },

  /* ----------------------------------------------------------- calculus II */
  /*
   * Five units, and the first one is longer than any unit elsewhere in the
   * platform on purpose. Techniques of integration is not five ideas; it is one
   * idea — every differentiation rule can be run backwards — met five times, and
   * splitting it into two units would put a course boundary in the middle of a
   * single argument.
   */
  {
    id: 'unit-integration-techniques',
    courseId: 'course-calculus-1',
    title: 'Integration, Properly',
    description:
      'Every rule of differentiation, run backwards: the chain rule as substitution, the product rule as parts, the identities as a way through the trigonometric integrals, and the algebra of a rational function as partial fractions — then the limits that let an integral run to infinity.',
    lessonIds: [
      'u-substitution-01',
      'integration-by-parts-01',
      'trigonometric-integrals-01',
      'trigonometric-substitution-01',
      'partial-fractions-01',
      'improper-integrals-01',
    ],
  },
  {
    id: 'unit-integral-applications',
    courseId: 'course-calculus-1',
    title: 'What an Integral Adds Up',
    description:
      'The integral stops being an area and becomes a way of adding up infinitely many small contributions: the length of a curve, the surface a spinning curve sweeps, the work a changing force does, and the point a shape balances on.',
    lessonIds: ['arc-length-01', 'surface-of-revolution-01', 'work-integrals-01', 'center-of-mass-01'],
  },
  {
    id: 'unit-infinite-series',
    courseId: 'course-calculus-1',
    title: 'Sums That Never Finish',
    description:
      'A list of numbers and whether it settles, the sum of that list and whether *it* settles — two different questions — and then the four tests that answer the second one when the first is not enough.',
    lessonIds: [
      'sequence-limits-01',
      'series-convergence-01',
      'integral-test-01',
      'comparison-tests-01',
      'ratio-test-01',
      'alternating-series-01',
    ],
  },
  {
    id: 'unit-power-series',
    courseId: 'course-calculus-1',
    title: 'Functions as Infinite Polynomials',
    description:
      'What happens when the terms of a series contain an x: a polynomial with no last term, the interval of x it is entitled to speak about, and the recipe that produces one from any function you can differentiate repeatedly.',
    lessonIds: ['power-series-01', 'taylor-series-01'],
  },
  {
    id: 'unit-parametric-polar',
    courseId: 'course-calculus-1',
    title: 'Curves Not Given by y = f(x)',
    description:
      'Two ways of describing a curve that a function of x cannot — by a parameter both coordinates depend on, and by an angle and a radius — and the calculus each of them needs.',
    lessonIds: ['parametric-curves-01', 'polar-calculus-01'],
  },

  /* -------------------------------------------------------- linear algebra */
  /*
   * Six units, and the first of them is where the branch leaves school
   * mathematics. `vectors` in precalculus drew arrows in the plane; this course
   * takes the components seriously and stops drawing, because the interesting
   * dimensions start at four.
   */
  {
    id: 'unit-la-vectors',
    courseId: 'course-linear-algebra',
    title: 'Vectors and What They Reach',
    description:
      'An arrow was always a list of numbers in disguise. Scale a few lists, add them up, and ask two questions that turn out to be the whole subject: what can you reach, and is any of it redundant?',
    lessonIds: ['vectors-rn-01', 'linear-combinations-01', 'span-01', 'linear-independence-01'],
  },
  {
    id: 'unit-la-matrices',
    courseId: 'course-linear-algebra',
    title: 'Matrices',
    description:
      'The notation that makes those questions computable — matrix arithmetic and the multiplication that refuses to commute, the three row moves, the elimination they power, the inverse, and the single number that says whether one exists.',
    lessonIds: [
      'matrices-01',
      'row-reduction-01',
      'gaussian-elimination-01',
      'matrix-inverse-01',
      'determinants-01',
    ],
  },
  {
    id: 'unit-la-systems',
    courseId: 'course-linear-algebra',
    title: 'Linear Systems',
    description:
      'Ax = b read two ways, the pivot count that predicts the answer before the answer is found, and why a system has none, one, or infinitely many solutions and never two.',
    lessonIds: ['linear-systems-01', 'rank-01', 'solution-sets-01'],
  },
  {
    id: 'unit-la-vector-spaces',
    courseId: 'course-linear-algebra',
    title: 'Vector Spaces',
    description:
      'The axioms, arrived at last rather than first, because by now three different things have been seen to obey them — then subspaces, bases, the theorem that makes dimension well defined, and coordinates in a basis you chose.',
    lessonIds: ['vector-spaces-01', 'subspaces-01', 'basis-01', 'dimension-01', 'coordinates-01'],
  },
  {
    id: 'unit-la-transformations',
    courseId: 'course-linear-algebra',
    title: 'Linear Transformations',
    description:
      'Functions that respect addition and scaling, what they destroy and what they reach, and the fact that where the basis vectors land is the entire map.',
    lessonIds: ['linear-transformations-01', 'kernel-range-01', 'transformation-matrix-01'],
  },
  {
    id: 'unit-la-eigen',
    courseId: 'course-linear-algebra',
    title: 'Eigenvalues and Eigenvectors',
    description:
      'The directions a transformation does not turn, the scale factors along them, the change of basis that makes a matrix diagonal — and the long-run behaviour that falls out once it is.',
    lessonIds: ['eigenvalues-01', 'eigenvectors-01', 'diagonalization-01', 'eigen-applications-01'],
  },

  /* ----------------------------------------------------------- algebra II */
  {
    id: 'unit-complex-polynomials',
    courseId: 'course-algebra-2',
    title: 'Complex Numbers and Polynomials',
    description: 'Finishing the number system, and what that lets you say about a curve of any degree.',
    lessonIds: ['complex-numbers-01', 'polynomial-division-01', 'polynomial-functions-01'],
  },
  {
    id: 'unit-rational-radical',
    courseId: 'course-algebra-2',
    title: 'Rational and Radical',
    description:
      'What a fractional exponent has to mean, and the three places where the domain bites back: a forbidden input, an answer that is not one, and a root that was never allowed.',
    lessonIds: [
      'rational-exponents-01',
      'rational-functions-01',
      'rational-equations-01',
      'radical-functions-01',
      'radical-equations-01',
    ],
  },
  {
    id: 'unit-function-shapes',
    courseId: 'course-algebra-2',
    title: 'Moving and Undoing Functions',
    description:
      'One set of rules for shifting and stretching every curve, what it means to run one backwards, and what happens when a function needs more than one rule.',
    lessonIds: ['function-transformations-01', 'function-operations-01', 'inverse-functions-01', 'piecewise-functions-01'],
  },
  {
    id: 'unit-logarithms',
    courseId: 'course-algebra-2',
    title: 'Exponentials and Logarithms',
    description:
      'What a bank does with a rate that the formula y = a·bˣ does not, the number that appears when compounding never stops, the inverse of the exponential, and why its three laws are exponent rules read backwards.',
    lessonIds: ['exponential-models-01', 'logarithms-01', 'exponential-equations-01', 'natural-logarithm-01'],
  },
  {
    id: 'unit-systems-inequalities',
    courseId: 'course-algebra-2',
    title: 'When the Answer Is Not a Number',
    description:
      'A solution that is a stretch of the number line, and a solution that is two points at once — the year where "solve" stops meaning "find the value of x".',
    lessonIds: ['polynomial-inequalities-01', 'nonlinear-systems-01'],
  },
  {
    id: 'unit-sequences-11',
    courseId: 'course-algebra-2',
    title: 'Sequences That Define Themselves',
    description:
      'The other way to write down a pattern — each term from the one before — which is how you notice a sequence, and sometimes the only definition it has.',
    lessonIds: ['recursive-sequences-01'],
  },
  {
    id: 'unit-trig-functions',
    courseId: 'course-algebra-2',
    title: 'Trigonometry Beyond the Triangle',
    description:
      'Sine and cosine for every angle there is, the wave that appears when you unroll the circle, and the third ratio — a quotient, with the gaps a quotient brings.',
    lessonIds: ['unit-circle-01', 'trig-graphs-01', 'tangent-function-01'],
  },
  {
    id: 'unit-data-eleven',
    courseId: 'course-algebra-2',
    title: 'Distributions and Fit',
    description:
      'What a standard deviation is actually for: the bell-shaped curve an enormous number of measurements fall into, and the line that summarises a cloud of points along with the number that says whether to believe it.',
    lessonIds: ['normal-distribution-01', 'regression-01'],
  },

  /* ------------------------------------------------------------ geometry */
  {
    id: 'unit-proof',
    courseId: 'course-geometry',
    title: 'Reasoning, Proof and Parallel Lines',
    description: 'The move from measuring a figure to arguing about every figure of its kind — and the four sentences one theorem turns into.',
    lessonIds: ['geometric-proof-01', 'conditional-statements-01', 'parallel-lines-01'],
  },
  {
    id: 'unit-triangles',
    courseId: 'course-geometry',
    title: 'Congruence and Similarity',
    description: 'When three measurements fix a triangle, what follows from that, what a compass can build from it, what happens when only the shape matches, and how a parallel line cuts the sides.',
    lessonIds: ['triangle-congruence-01', 'triangle-properties-01', 'constructions-01', 'similarity-01', 'triangle-proportionality-01'],
  },
  {
    id: 'unit-polygons-circles',
    courseId: 'course-geometry',
    title: 'Polygons, Quadrilaterals and Circles',
    description: 'Angle sums for any number of sides, what each quadrilateral name guarantees, the theorems the circle\u2019s symmetry produces, the lengths cut by lines through it, and the arithmetic of a slice of one.',
    lessonIds: ['polygons-01', 'quadrilateral-properties-01', 'circle-theorems-01', 'chords-secants-01', 'arc-sector-01'],
  },
  {
    id: 'unit-coordinate-trig',
    courseId: 'course-geometry',
    title: 'Coordinates and Trigonometry',
    description: 'Geometry done with algebra, the ratios that turn an angle into a length, and running those ratios backwards to recover the angle.',
    lessonIds: ['coordinate-geometry-01', 'special-triangles-01', 'trig-ratios-01', 'trig-applications-01'],
  },

  /* ----------------------------------------------------------- algebra I */
  {
    id: 'unit-real-numbers',
    courseId: 'course-algebra-1',
    title: 'The Real Numbers',
    description: 'Which families a number belongs to, and the rules that let an expression be rewritten at all.',
    lessonIds: ['real-numbers-01'],
  },
  {
    id: 'unit-linear-forms',
    courseId: 'course-algebra-1',
    title: 'Linear Equations, Inequalities and Forms',
    description: 'Clearing denominators, two conditions at once, and the three ways of writing one straight line.',
    lessonIds: ['equations-with-fractions-01', 'compound-inequalities-01', 'linear-equation-forms-01'],
  },
  {
    id: 'unit-algebra-foundations',
    courseId: 'course-algebra-1',
    title: 'Formulas, Functions and Regions',
    description: 'Solving for the letter you want, saying which inputs a function allows, and inequalities whose answer is half the plane.',
    lessonIds: ['literal-equations-01', 'domain-range-01', 'two-variable-inequalities-01'],
  },
  {
    id: 'unit-polynomials',
    courseId: 'course-algebra-1',
    title: 'Polynomials, Factoring and Radicals',
    description: 'Multiplying expressions out, running that backwards, and writing irrational numbers exactly.',
    lessonIds: ['polynomials-01', 'factoring-01', 'radicals-01'],
  },
  {
    id: 'unit-quadratics',
    courseId: 'course-algebra-1',
    title: 'Quadratic Equations and Graphs',
    description: 'Two solutions instead of one, the curve that explains why, the rebuild that finds the vertex, and the formula that always works.',
    lessonIds: ['quadratics-01', 'quadratic-graphs-01', 'completing-the-square-01', 'quadratic-formula-01'],
  },
  {
    id: 'unit-growth',
    courseId: 'course-algebra-1',
    title: 'Exponential Change and Sequences',
    description: 'Growth that multiplies rather than adds, and the patterns that follow from each kind.',
    lessonIds: ['exponential-functions-01', 'sequences-01'],
  },
  {
    id: 'unit-rational-algebra',
    courseId: 'course-algebra-1',
    title: 'Rational Expressions and Absolute Value',
    description: 'Algebraic fractions where only factors cancel, and equations and inequalities with two answers, none, or a region between them.',
    lessonIds: ['rational-expressions-01', 'absolute-value-01'],
  },
  {
    id: 'unit-data-spread',
    courseId: 'course-algebra-1',
    title: 'Distributions and Spread',
    description: 'The measure of spread that squares its distances rather than taking them positively — and what that buys.',
    lessonIds: ['standard-deviation-01'],
  },

  /* ------------------------------------------------------------- grade 8 */
  {
    id: 'unit-exponent-laws',
    courseId: 'course-grade-8',
    title: 'Exponents and Scientific Notation',
    description: 'The rules for combining powers, derived rather than memorised, and the notation they make possible.',
    lessonIds: ['exponent-rules-01', 'scientific-notation-01'],
  },
  {
    id: 'unit-roots',
    courseId: 'course-grade-8',
    title: 'Roots and the Pythagorean Theorem',
    description:
      'Undoing a square, undoing a cube, the numbers that turn out not to be fractions, the result that needs them, and the distance it measures on a grid.',
    lessonIds: ['square-roots-01', 'cube-roots-01', 'pythagoras-01', 'coordinate-distance-01'],
  },
  {
    id: 'unit-linear-equations',
    courseId: 'course-grade-8',
    title: 'Linear Equations in One Variable',
    description: 'Equations with the unknown on both sides, and what it means when it cancels out altogether.',
    lessonIds: ['linear-equation-solutions-01'],
  },
  {
    id: 'unit-functions',
    courseId: 'course-grade-8',
    title: 'Functions, Slope and Systems',
    description: 'A relationship as an object rather than a calculation — graphed, compared and solved.',
    lessonIds: ['functions-intro-01', 'linear-functions-01', 'comparing-functions-01', 'systems-of-equations-01'],
  },
  {
    id: 'unit-transformations',
    courseId: 'course-grade-8',
    title: 'Transformations and Solids',
    description: 'Congruence as motion, and the volumes of the three round solids.',
    lessonIds: ['transformations-01', 'round-solids-01'],
  },
  {
    id: 'unit-angle-geometry',
    courseId: 'course-grade-8',
    title: 'Angles, Parallels and Similarity',
    description: 'The angle facts that follow from two lines never meeting — and the two angles that fix a shape without fixing its size.',
    lessonIds: ['angle-relationships-01'],
  },
  {
    id: 'unit-bivariate-data',
    courseId: 'course-grade-8',
    title: 'Scatter Plots and Two-Way Tables',
    description: 'Two measurements at once — measured or merely counted — and the discipline of not claiming more than the data shows.',
    lessonIds: ['scatter-plots-01', 'two-way-tables-01'],
  },
  {
    id: 'unit-chance',
    courseId: 'course-grade-8',
    title: 'Chance in Practice',
    description: 'What the counting argument predicts, what the trials actually gave, and why the gap closes rather than cancels.',
    lessonIds: ['experimental-probability-01'],
  },

  /* ------------------------------------------------------------- grade 7 */
  {
    id: 'unit-rational-numbers',
    courseId: 'course-grade-7',
    title: 'Rational Numbers',
    description: 'Fractions, decimals and negatives finally treated as one system with one set of rules.',
    lessonIds: ['rational-numbers-01'],
  },
  {
    id: 'unit-proportional-reasoning',
    courseId: 'course-grade-7',
    title: 'Proportions and Percent Change',
    description: 'Scaling as an operation, and the arithmetic behind every discount, tax and interest rate.',
    lessonIds: ['proportions-01', 'proportional-relationships-01', 'percent-applications-01'],
  },
  {
    id: 'unit-linear-expressions',
    courseId: 'course-grade-7',
    title: 'Linear Expressions',
    description: 'Rewriting an expression into the form that answers the question, without changing what it means.',
    lessonIds: ['linear-expressions-01'],
  },
  {
    id: 'unit-equations',
    courseId: 'course-grade-7',
    title: 'Equations and Inequalities',
    description: 'Solving when the unknown is behind several operations — and answers that are ranges rather than values.',
    lessonIds: ['multi-step-equations-01', 'inequalities-01'],
  },
  {
    id: 'unit-angles-circles',
    courseId: 'course-grade-7',
    title: 'Angles and Circles',
    description: 'Finding angles you were never given, and the constant every circle shares.',
    lessonIds: ['angles-01', 'circles-01'],
  },
  {
    id: 'unit-scale-drawings',
    courseId: 'course-grade-7',
    title: 'Scale Drawings',
    description: 'Maps, plans and models — and why doubling every length does not double the area.',
    lessonIds: ['scale-drawings-01'],
  },
  {
    id: 'unit-solids',
    courseId: 'course-grade-7',
    title: 'Surface Area',
    description: 'Covering a solid rather than filling it — and why the units settle the question.',
    lessonIds: ['surface-area-01'],
  },
  {
    id: 'unit-probability',
    courseId: 'course-grade-7',
    title: 'Probability',
    description: 'Measuring how likely something is, on a scale from 0 to 1 — for one event and then for two.',
    lessonIds: ['basic-probability-01', 'compound-probability-01'],
  },
  {
    id: 'unit-samples',
    courseId: 'course-grade-7',
    title: 'Samples and Inferences',
    description: 'Claiming something about a whole population from the part of it you managed to measure.',
    lessonIds: ['sampling-01'],
  },

  /* ------------------------------------------------------------- grade 6 */
  {
    id: 'unit-fraction-division',
    courseId: 'course-grade-6',
    title: 'Dividing Fractions',
    description: 'The last of the four operations on fractions, and the one that breaks a familiar rule.',
    lessonIds: ['divide-fractions-01'],
  },
  {
    id: 'unit-decimal-operations',
    courseId: 'course-grade-6',
    title: 'Decimal Arithmetic',
    description: 'Multiplying and dividing decimals, where the only new question is where the point goes.',
    lessonIds: ['decimal-operations-01'],
  },
  {
    id: 'unit-integers',
    courseId: 'course-grade-6',
    title: 'Negative Numbers',
    description: 'Extending the number line below zero, arithmetic across it, and distance from zero.',
    lessonIds: ['integers-01', 'absolute-value-opposites-01'],
  },
  {
    id: 'unit-exponents',
    courseId: 'course-grade-6',
    title: 'Exponents, Primes and Common Factors',
    description: 'Repeated multiplication written compactly, the atoms every number is built from, and what two numbers share.',
    lessonIds: ['exponents-01', 'prime-factorization-01', 'gcf-lcm-01'],
  },
  {
    id: 'unit-ratios',
    courseId: 'course-grade-6',
    title: 'Ratios and Rates',
    description: 'Comparing by division rather than by difference.',
    lessonIds: ['ratios-01', 'unit-rates-01'],
  },
  {
    id: 'unit-percent',
    courseId: 'course-grade-6',
    title: 'Percentages',
    description: 'Ratios fixed at a denominator of one hundred, and what that buys you.',
    lessonIds: ['percentages-01'],
  },
  {
    id: 'unit-expressions',
    courseId: 'course-grade-6',
    title: 'Algebraic Expressions',
    description: 'Reading, writing and simplifying expressions before solving anything.',
    lessonIds: ['algebraic-expressions-01'],
  },
  {
    id: 'unit-inequalities-relationships',
    courseId: 'course-grade-6',
    title: 'Inequalities and Relationships',
    description: 'Naming a whole range of numbers, and describing how one quantity depends on another.',
    lessonIds: ['one-variable-inequalities-01', 'two-variable-relationships-01'],
  },
  {
    id: 'unit-coordinate-geometry',
    courseId: 'course-grade-6',
    title: 'The Coordinate Plane',
    description: 'Giving every point on a plane a name.',
    lessonIds: ['coordinate-plane-01'],
  },
  {
    id: 'unit-area-volume',
    courseId: 'course-grade-6',
    title: 'Area and Volume',
    description: 'Beyond the rectangle, and into three dimensions.',
    lessonIds: ['area-triangles-01', 'volume-01'],
  },
  {
    id: 'unit-statistics',
    courseId: 'course-grade-6',
    title: 'Data, Averages and Spread',
    description: 'Summarising a data set, noticing what each summary hides, and measuring what is left.',
    lessonIds: ['data-displays-01', 'mean-median-mode-01', 'histograms-01', 'data-variability-01'],
  },

  /* ---------------------------------------------------------- grades 3-5 */
  {
    id: 'unit-number-sense',
    courseId: 'course-grade-3',
    title: 'Number Sense',
    description: 'What numbers are, how our number system writes them, and how to round them.',
    lessonIds: ['counting-01', 'place-value-01', 'rounding-01'],
  },
  {
    id: 'unit-whole-number-operations',
    courseId: 'course-grade-3',
    title: 'The Four Operations',
    description: 'Adding, subtracting, multiplying and dividing whole numbers.',
    lessonIds: [
      'multi-digit-addition-01',
      'multi-digit-subtraction-01',
      'multiplication-facts-01',
      'division-meaning-01',
      'division-remainders-01',
    ],
  },
  {
    id: 'unit-multiplication-strategies',
    courseId: 'course-grade-3',
    title: 'Multiplication Strategies',
    description: 'The properties that turn a table of facts into a way of calculating.',
    lessonIds: ['multiplication-properties-01', 'multiply-by-tens-01', 'fact-families-01'],
  },
  {
    id: 'unit-problem-solving',
    courseId: 'course-grade-3',
    title: 'Patterns and Two-Step Problems',
    description: 'Finding the rule behind a sequence, and the question hidden inside a question.',
    lessonIds: ['number-patterns-01', 'two-step-problems-01'],
  },
  {
    id: 'unit-fractions-meaning',
    courseId: 'course-grade-3',
    title: 'What Fractions Are',
    description: 'The meaning of a fraction, and its place on the number line, before any arithmetic is done with one.',
    lessonIds: ['fraction-basics-01', 'fractions-on-number-line-01', 'whole-number-fractions-01'],
  },
  {
    id: 'unit-measurement',
    courseId: 'course-grade-3',
    title: 'Perimeter and Area',
    description: 'Measuring the edge of a shape, the space inside it, and the space inside a shape made of several.',
    lessonIds: ['perimeter-01', 'area-rectangles-01', 'area-composite-01'],
  },
  {
    id: 'unit-shapes',
    courseId: 'course-grade-3',
    title: 'Shapes and Their Parts',
    description: 'Naming a shape by what is true of it, and cutting one into equal shares.',
    lessonIds: ['shape-attributes-01', 'equal-shares-01'],
  },
  {
    id: 'unit-time-measurement',
    courseId: 'course-grade-3',
    title: 'Time, Mass and Volume',
    description: 'Reading a clock, working out how long something took, and measuring what things weigh and hold.',
    lessonIds: ['telling-time-01', 'elapsed-time-01', 'mass-and-capacity-01'],
  },
  {
    id: 'unit-data-grade-3',
    courseId: 'course-grade-3',
    title: 'Graphs and Data',
    description: 'Reading a picture graph, a bar graph and a line plot — and answering the question that was asked.',
    lessonIds: ['scaled-graphs-01', 'line-plots-01'],
  },
  {
    id: 'unit-multi-digit-arithmetic',
    courseId: 'course-grade-4',
    title: 'Multiplication and Division at Scale',
    description: 'The year multiplication and division stop being facts and become methods you can run on any pair of numbers.',
    lessonIds: ['multiplicative-comparison-01', 'multiply-multi-digit-01', 'long-division-01'],
  },
  {
    id: 'unit-fraction-operations',
    courseId: 'course-grade-4',
    title: 'First Fraction Arithmetic',
    description: 'Adding, subtracting and multiplying when the pieces are already the same size — the arithmetic before common denominators.',
    lessonIds: ['add-like-fractions-01', 'fraction-times-whole-01'],
  },
  {
    id: 'unit-measure-geometry-4',
    courseId: 'course-grade-4',
    title: 'Measurement, Angles and Symmetry',
    description: 'Converting between units, measuring turn with a protractor, and testing a shape by folding it.',
    lessonIds: ['unit-conversion-01', 'angle-measure-01', 'symmetry-01'],
  },
  {
    id: 'unit-fractions-equivalence',
    courseId: 'course-grade-4',
    title: 'Equivalence and Simplifying',
    description: 'Different names for the same amount, and how to choose the simplest one.',
    lessonIds: ['equivalent-fractions-01', 'simplify-fractions-01'],
  },
  {
    id: 'unit-fractions-compare',
    courseId: 'course-grade-4',
    title: 'Comparing Fractions',
    description: 'Deciding which fraction is larger, and making pieces the same size.',
    lessonIds: ['compare-fractions-01', 'common-denominators-01'],
  },
  {
    id: 'unit-factors',
    courseId: 'course-grade-4',
    title: 'Factors, Multiples and Order',
    description: 'The building blocks of numbers, which of them cannot be built at all, and the convention for reading expressions.',
    lessonIds: ['factors-multiples-01', 'prime-composite-01', 'order-of-operations-01'],
  },
  {
    id: 'unit-decimals',
    courseId: 'course-grade-4',
    title: 'Decimals',
    description: 'Place value beyond the ones, the fractions hiding behind it, and arithmetic with decimals.',
    lessonIds: ['decimal-place-value-01', 'fractions-to-decimals-01', 'compare-decimals-01', 'add-decimals-01'],
  },
  {
    id: 'unit-powers-of-ten-5',
    courseId: 'course-grade-5',
    title: 'Powers of Ten',
    description: 'Why multiplying by a hundred is a move rather than a calculation, and how to round the places smaller than one.',
    lessonIds: ['powers-of-ten-01', 'round-decimals-01'],
  },
  {
    id: 'unit-decimal-operations-5',
    courseId: 'course-grade-5',
    title: 'Multiplying and Dividing Decimals',
    description: 'Counting decimal places into a product, and shifting both numbers until a divisor is whole.',
    lessonIds: ['multiply-decimals-01', 'divide-decimals-01'],
  },
  {
    id: 'unit-fractions-arithmetic',
    courseId: 'course-grade-5',
    title: 'Fraction Arithmetic',
    description: 'Adding, subtracting and multiplying fractions, mixed numbers, and the division that a fraction bar has always been.',
    lessonIds: ['add-fractions-01', 'multiply-fractions-01', 'mixed-numbers-01', 'fraction-as-division-01', 'add-mixed-numbers-01'],
  },
  {
    id: 'unit-fraction-scaling-5',
    courseId: 'course-grade-5',
    title: 'Scaling and Dividing With Fractions',
    description: 'Multiplication that makes a number smaller, and division that makes one bigger.',
    lessonIds: ['fraction-scaling-01', 'divide-unit-fractions-01'],
  },
  {
    id: 'unit-intro-algebra',
    courseId: 'course-grade-5',
    title: 'Expressions and the First Algebra',
    description: 'Brackets as punctuation, letters for unknown numbers, and solving simple equations.',
    lessonIds: ['numerical-expressions-01', 'variables-01', 'one-step-equations-01'],
  },
  {
    id: 'unit-volume-5',
    courseId: 'course-grade-5',
    title: 'Volume',
    description: 'Filling a solid with unit cubes, and adding up the boxes a real object is made of.',
    lessonIds: ['volume-unit-cubes-01', 'volume-composite-01'],
  },
  {
    id: 'unit-coordinate-plane-5',
    courseId: 'course-grade-5',
    title: 'Patterns and the Coordinate Plane',
    description: 'Two rules running side by side, and the grid that turns their pairs of terms into a picture.',
    lessonIds: ['number-pattern-pairs-01', 'coordinate-first-quadrant-01'],
  },
  {
    id: 'unit-classify-shapes-5',
    courseId: 'course-grade-5',
    title: 'Classifying Shapes',
    description: 'Shape families inside shape families, and why "is a" runs one way only.',
    lessonIds: ['classify-quadrilaterals-01'],
  },
];
